import React from 'react'
import { v4 as uuidv4 } from 'uuid'
import { message, Modal as AntdModal } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { convertToCommaSeparatedApnUtil } from '../../utils/functionUtils'
import {
  handleGenerateLabelsUtil,
  saveLabelRequest
} from '../../api/shelfEdgeApiService/shelfEdgeLabellingApiUtil'
import { fetchAccessTokenForCognitoUser } from '../../utils/authUtils'

/**
 * Detects if the current user is authenticated with Cognito
 * @param {Object} instance - MSAL instance
 * @returns {Promise<boolean>} - True if the user is authenticated with Cognito
 */
export const detectCognitoUser = async (instance) => {
  try {
    // Try to get a Cognito token
    const cognitoToken = await fetchAccessTokenForCognitoUser()

    // If we have a Cognito token, then the user is a Cognito user
    if (cognitoToken) {
      return true
    }

    // If we have an MSAL instance with the expected methods, it's an AD user
    if (instance && typeof instance.acquireTokenSilent === 'function') {
      return false
    }

    // Default: we can't determine, so assume it's not a Cognito user
    return false
  } catch (error) {
    console.error('Error detecting authentication type:', error)
    return false
  }
}

/**
 * Handles generating shelf edge labels with validation for all users
 * @param {Object} instance - MSAL instance
 * @param {Function} setIsReloading - State setter for loading state
 * @param {Array} rows - Current rows of APNs
 * @param {Array} selectedRows - Selected row IDs
 * @param {Function} setProductData - State setter for product data
 * @param {Function} setMissingAPNs - State setter for missing APNs
 * @param {Function} setPrintOpen - State setter for print dialog
 * @param {boolean} isCognitoUser - Flag for API authentication (validation applies to all users)
 * @returns {Promise<Object|null>} - API response or null if failed
 */

export const handleGenerateLabels = async (
  instance,
  setIsReloading,
  rows,
  selectedRows,
  setProductData,
  setMissingAPNs,
  setPrintOpen,
  isCognitoUser = false
) => {
  try {
    setIsReloading(true)

    // Prepare the selected APNs with quantities
    const selectedApns = selectedRows
      .map((rowId) => {
        const row = rows.find((r) => r.id === rowId)
        return row ? { apn: row.apn, quantity: row.quantity } : null
      })
      .filter(Boolean)

    // Call the API to get product data
    const response = await handleGenerateLabelsUtil(
      instance,
      setProductData,
      selectedApns,
      isCognitoUser
    )

    if (response?.status === 200) {
      // Helper function to check if a product is valid
      const isValidProduct = (product) => {
        // Validate APN
        if (!product.APN || isNaN(product.APN) || product.APN <= 0) {
          return false
        }

        // Validate ItemCode
        if (!product.ItemCode || isNaN(product.ItemCode) || product.ItemCode <= 0) {
          return false
        }

        // Validate ItemDescription
        if (!product.ItemDescription || product.ItemDescription.trim() === '') {
          return false
        }

        // Validate RecRetailIncPrice
        if (!product.ItemPrices?.ItemPrice?.RecRetailIncPrice ||
          isNaN(product.ItemPrices.ItemPrice.RecRetailIncPrice) ||
          product.ItemPrices.ItemPrice.RecRetailIncPrice <= 0) {
          return false
        }

        // Validate DepartmentCode first, then ReorderBuyerCode as fallback
        const departmentCode = product.DepartmentCode
        const reorderBuyerCode = product.ReorderBuyerCode

        // Check if DepartmentCode is valid
        const hasDepartmentCode = departmentCode !== null &&
          departmentCode !== undefined &&
          departmentCode !== '' &&
          departmentCode.toString().trim() !== ''

        // Check if ReorderBuyerCode is valid
        const hasReorderBuyerCode = reorderBuyerCode !== null &&
          reorderBuyerCode !== undefined &&
          reorderBuyerCode !== '' &&
          reorderBuyerCode.toString().trim() !== ''

        // If neither DepartmentCode nor ReorderBuyerCode is valid, item is invalid
        if (!hasDepartmentCode && !hasReorderBuyerCode) {
          return false
        }

        return true
      }

      // Filter valid and invalid products
      const validProducts = response.data.productInfo.filter(isValidProduct)
      const invalidProducts = response.data.productInfo.filter(product => !isValidProduct(product))

      // Get APNs that were not returned by API
      const returnedApns = response.data.productInfo.map(p => p.APN)
      const notFoundApns = selectedApns
        .filter(item => !returnedApns.includes(parseInt(item.apn, 10)))
        .map(item => item.apn)

      // Combine all invalid APNs
      const allInvalidApns = [
        ...invalidProducts.map(p => p.APN.toString()),
        ...notFoundApns
      ]

      // Set state
      setProductData(validProducts)
      setMissingAPNs(allInvalidApns)

      // Handle validation results
      if (validProducts.length === 0) {
        message.error('No valid products found for printing. Please check the APN list.')
        return null
      }

      if (allInvalidApns.length > 0) {
        message.warning(`${allInvalidApns.length} invalid APN(s) found. Only valid products will be printed.`)
      }

      setPrintOpen(true)
      return response
    } else {
      message.error('Please provide valid APN list')
      return null
    }
  } catch (error) {
    console.error('Error generating labels:', error)
    message.error('Failed to generate labels')
    return null
  } finally {
    setIsReloading(false)
  }
}

/**
 * Handles quantity change for a row
 * @param {String} id - Row ID 
 * @param {Number} value - New quantity value
 * @param {Function} setRows - State setter for rows
 */
export const handleQuantityChange = (id, value, setRows) => {
  if (value < 1 || isNaN(value)) {
    message.error('Quantity cannot be less than 1!')
    setRows((prevRows) =>
      prevRows.map((row) => (row.id === id ? { ...row, quantity: 1 } : row))
    )
    return
  }
  setRows((prevRows) =>
    prevRows.map((row) => (row.id === id ? { ...row, quantity: value } : row))
  )
}

/**
 * Handles saving APNs from input
 * @param {String} apnInput - Input APN string
 * @param {Function} setError - State setter for error message
 * @param {Function} setRows - State setter for rows
 * @param {Function} setOpen - State setter for modal visibility
 * @param {Function} setApnInput - State setter for APN input
 */
export const handleSaveAPN = (
  apnInput,
  setError,
  setRows,
  setOpen,
  setApnInput
) => {
  setError('')
  if (!apnInput) {
    setError('Please enter APN(s) before saving.')
    return
  }

  const processedApns = convertToCommaSeparatedApnUtil(apnInput)
  if (processedApns.length === 0) {
    setError('Please enter valid 13-digit APN(s)')
    return
  }

  setRows((prevRows) => {
    const updatedRows = [...prevRows]
    processedApns.forEach((newApn) => {
      const existingRow = updatedRows.find(
        (row) => parseInt(row.apn) === parseInt(newApn)
      )
      if (existingRow) {
        existingRow.quantity += 1
      } else {
        updatedRows.push({ id: uuidv4(), apn: newApn, quantity: 1 })
      }
    })
    return updatedRows
  })

  setOpen(false)
  setApnInput('')
}

/**
 * Handles saving a label request
 * @param {String} requestName - Name of the request
 * @param {String} userName - User's name
 * @param {Array} rows - Current rows of APNs
 * @param {Object} instance - MSAL instance
 * @param {Function} setLoading - State setter for loading state
 * @param {Function} setSaveDialogOpen - State setter for save dialog visibility
 * @param {Function} setRequestName - State setter for request name
 * @param {Function} setImageDialogOpen - State setter for success image dialog
 * @param {boolean} isCognitoUser - Flag indicating if user is authenticated with Cognito
 */
export const handleSaveRequest = async (
  requestName,
  userName,
  rows,
  instance,
  setLoading,
  setSaveDialogOpen,
  setRequestName,
  setImageDialogOpen,
  isCognitoUser = false
) => {
  if (!requestName.trim() || !userName.trim()) {
    message.error('Both request name and user name are required.')
    return
  }

  try {
    setLoading(true)

    const data = {
      id: uuidv4(),
      listName: requestName,
      printList: rows.map((row) => ({
        apn: row.apn,
        quantity: row.quantity
      })),
      createdBy: userName
    }

    await saveLabelRequest(data, instance, 'update', isCognitoUser)
    setSaveDialogOpen(false)
    setRequestName('')
    setImageDialogOpen(true)
    message.success('Request saved successfully')
  } catch (error) {
    console.error('Error saving request:', error)
    message.error('Failed to save request')
  } finally {
    setLoading(false)
  }
}

/**
 * Handles barcode scan result
 * @param {Array} barcodeResult - Scan result
 * @param {Set} scannedApns - Set of already scanned APNs
 * @param {Function} setRows - State setter for rows
 * @param {Function} setScannedApns - State setter for scanned APNs set
 * @param {Function} setIsScannerActive - State setter for scanner activity
 */
export const handleBarcodeScan = (
  barcodeResult,
  scannedApns,
  setRows,
  setScannedApns,
  setIsScannerActive
) => {
  setIsScannerActive(false)
  if (barcodeResult?.length) {
    const apn = parseInt(barcodeResult[0].text)
    if (!scannedApns.has(apn)) {
      setRows((prevRows) => {
        const existingRow = prevRows.find((row) => parseInt(row.apn) === apn)
        if (existingRow) {
          return prevRows.map((row) =>
            parseInt(row.apn) === apn
              ? { ...row, quantity: row.quantity + 1 }
              : row
          )
        }
        return [...prevRows, { id: uuidv4(), apn, quantity: 1 }]
      })
      setScannedApns((prev) => new Set(prev).add(apn))
    } else {
      setRows((prevRows) =>
        prevRows.map((row) =>
          parseInt(row.apn) === apn
            ? { ...row, quantity: row.quantity + 1 }
            : row
        )
      )
    }
  } else {
    message.error('No barcode detected. Please scan again.')
  }
}

/**
 * Exports missing APNs as a CSV file
 * @param {Array} missingAPNs - List of missing APNs
 */
export const exportMissingAPNs = (missingAPNs) => {
  const csvContent =
    'data:text/csv;charset=utf-8,' +
    missingAPNs.map((apn) => `"=""${apn}"""`).join('\n')
  const encodedUri = encodeURI(csvContent)
  const link = document.createElement('a')
  link.setAttribute('href', encodedUri)
  link.setAttribute('download', 'Invalid Apns.csv')
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/**
 * Handles deleting selected rows
 * @param {Array} selectedRows - Selected row IDs
 * @param {Function} setRows - State setter for rows
 * @param {Function} setSelectedRows - State setter for selected rows
 */
export const handleDeleteRows = (selectedRows, setRows, setSelectedRows) => {
  AntdModal.confirm({
    title: 'Confirm Deletion',
    icon: <ExclamationCircleOutlined />,
    content: `Do you want to delete ${selectedRows.length} ${selectedRows.length === 1 ? 'APN' : 'APNs'
      }?`,
    okText: 'Delete',
    okButtonProps: {
      danger: true,
      style: { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f' }
    },
    onOk: () => {
      setRows((prevRows) =>
        prevRows.filter((row) => !selectedRows.includes(row.id))
      )
      setSelectedRows([])
      message.success(
        `Successfully deleted ${selectedRows.length} ${selectedRows.length === 1 ? 'APN' : 'APNs'
        }`
      )
    }
  })
}

/**
 * Returns selected APNs with quantities
 * @param {Array} rows - Current rows of APNs
 * @param {Array} selectedRows - Selected row IDs
 * @returns {Array} - Selected APNs with quantities
 */
export const getSelectedApns = (rows, selectedRows) => {
  return selectedRows
    .map((rowId) => {
      const row = rows.find((r) => r.id === rowId)
      return row ? { apn: row.apn, quantity: row.quantity } : null
    })
    .filter(Boolean)
}